import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import PropTypes from "prop-types";
import { Controller, useFormContext } from "react-hook-form";
import { clsx } from "clsx";
import get from "lodash/get";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
} from "@/components/ui/combobox";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";
import { MultipleFilterTrigger } from "components/NewAdvancedSearch/filters/MultipleFilterTrigger";
import { useStickyChipScroll } from "components/NewAdvancedSearch/filters/useStickyChipScroll";
import NextPageStatus from "components/tags/NextPageStatus";
import { getTagStyle } from "helpers/tags";
import { useApplyTagOptions } from "hooks/useApplyTagOptions";
import { useInfiniteScrollSentinel } from "hooks/useInfiniteScrollSentinel";
import { useApplyTagDialog } from "providers/ApplyTagDialogProvider";
import TagService from "services/Tag";

import { REQUIRED_MARK } from "./fieldClasses";

const EMPTY = [];
const MAX_FLAGS_PER_PAGE = 100;

// MultipleFilterTrigger is styled for the filter panel: flex-1 collapses its
// height inside a Field's column flex, and the panel's background reads darker
// than a form card. These bring it level with the other fields' controls.
const TRIGGER_IN_FORM =
  "tw:w-full tw:flex-none tw:bg-transparent tw:dark:bg-input/30";

// The "M" disc the Semantic TagFormField drew, kept so the indicator reads the
// same across both tag fields.
const MetadataIcon = () => (
  <span
    role="img"
    aria-label="Has metadata fields"
    className="tw:flex tw:size-4 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-(--color-orange) tw:text-[10px] tw:leading-none tw:font-semibold tw:text-white tw:shadow-sm"
  >
    M
  </span>
);

/**
 * Multi-select for tags, rendering each selection as a chip in the tag's own
 * color. The options for `modelType` load a page at a time as the list
 * scrolls, and the stored value is an array of tag records.
 *
 * A selected tag with metadata fields opens the Apply Tag dialog when its chip
 * is clicked. The dialog loads the fields itself, and the values are stored on
 * that tag's record as `tag_metadata_values`, the shape the tag endpoints
 * accept.
 *
 * Only enabled tags can be selected. A disabled tag already on the record is
 * kept in the value and rendered as a muted chip that cannot be removed.
 *
 * `withMetadata={false}` is for a value stored as bare tag ids, which has no
 * place for metadata: the icons, the dialog, and the flags request are skipped.
 */
const FormTag = ({
  name,
  label = "Tags",
  required,
  modelType,
  placeholder = "Select Tags",
  disabled,
  withMetadata = true,
  id,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = get(errors, name);
  const inputId = id ?? name;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Field data-invalid={error ? true : undefined}>
          {label && (
            <FieldLabel
              htmlFor={inputId}
              className={required ? REQUIRED_MARK : undefined}
            >
              {label}
            </FieldLabel>
          )}
          <TagCombobox
            inputId={inputId}
            selected={Array.isArray(field.value) ? field.value : []}
            modelType={modelType}
            placeholder={placeholder}
            disabled={disabled}
            withMetadata={withMetadata}
            invalid={!!error}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
          <FieldError>{error?.message}</FieldError>
        </Field>
      )}
    />
  );
};

// Split out so the hooks can key off the selection without running inside
// Controller's render callback.
const TagCombobox = ({
  inputId,
  selected,
  modelType,
  placeholder,
  disabled,
  withMetadata,
  invalid,
  onChange,
  onBlur,
}) => {
  const openApplyTagDialog = useApplyTagDialog();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { ref: chipsRef, onScroll: onChipsScroll } = useStickyChipScroll(
    selected.length
  );

  const optionsQuery = useApplyTagOptions({
    modelType,
    search,
    enabled: isOpen,
  });
  const options = optionsQuery.data ?? EMPTY;
  const sentinelRef = useInfiniteScrollSentinel({
    hasNextPage: optionsQuery.hasNextPage,
    isFetchingNextPage: optionsQuery.isFetchingNextPage,
    isError: optionsQuery.isFetchNextPageError,
    fetchNextPage: optionsQuery.fetchNextPage,
    loadedCount: options.length,
  });

  const selectedIds = selected.map(tag => tag.id);
  const selectedById = new Map(selected.map(tag => [tag.id, tag]));
  // The backend keeps a disabled tag attached on save whatever the payload
  // says, so the field shows it but never lets it be removed or edited.
  const lockedTags = selected.filter(tag => !tag.enabled);
  const editableIds = selected
    .filter(tag => tag.enabled)
    .map(tag => tag.id)
    .sort();

  // A selected tag may sit on a page not loaded yet, so whether it has
  // metadata is asked for by id. The key matches ApplyTagsDropdown's, so the
  // two share the cache. It loads with the form rather than on open, so the
  // chips' edit buttons are ready when the list opens, and it keeps the
  // previous answer while a changed selection refetches.
  const flagsQuery = useQuery({
    queryKey: ["tags", "apply", "flags", modelType, false, editableIds],
    queryFn: () =>
      TagService.getTagsPage({
        model: modelType,
        page: 1,
        perPage: MAX_FLAGS_PER_PAGE,
        ids: editableIds,
      }),
    select: ({ data }) => new Map(data.map(tag => [tag.id, tag.has_metadata])),
    placeholderData: keepPreviousData,
    enabled: withMetadata && editableIds.length > 0,
  });

  const recordFor = tagId =>
    selectedById.get(tagId) ?? options.find(option => option.id === tagId);

  const labelFor = tagId => recordFor(tagId)?.name ?? tagId;

  const isLocked = tagId => recordFor(tagId)?.enabled === false;

  const styleFor = tagId => ({
    ...getTagStyle(recordFor(tagId)),
    ...(isLocked(tagId) && { fontStyle: "italic" }),
  });

  const hasMetadata = tagId =>
    withMetadata &&
    !isLocked(tagId) &&
    Boolean(flagsQuery.data?.get(tagId) ?? recordFor(tagId)?.has_metadata);

  // `tags` is passed in rather than read from `selected`: the dialog submits
  // after the render that opened it, and a tag picked in that render is not in
  // that render's `selected` yet.
  const storeMetadataValues = (tags, tagId, metadata) => {
    const tagMetadataValues = metadata.map(field => ({
      field_id: field.id,
      field_name: field.field_name,
      value: field.value,
    }));
    onChange(
      tags.map(tag =>
        tag.id === tagId
          ? { ...tag, tag_metadata_values: tagMetadataValues }
          : tag
      )
    );
  };

  const openMetadataDialog = (tags, tag) =>
    openApplyTagDialog(
      tag,
      metadata => storeMetadataValues(tags, tag.id, metadata),
      modelType
    );

  const editMetadata = tagId =>
    openMetadataDialog(selected, selectedById.get(tagId));

  // base-ui reports the selection as ids, so an id already held keeps its record
  // — and with it any metadata values — instead of being replaced by a fresh
  // copy from `options`. Locked tags are re-appended so that no keyboard
  // removal base-ui allows can drop them. Picking a tag with metadata opens
  // the dialog straight away; cancelling it keeps the tag selected.
  const handleValueChange = nextIds => {
    const picked = nextIds.map(recordFor).filter(tag => tag?.enabled);
    const nextTags = [...picked, ...lockedTags];
    onChange(nextTags);

    const added = picked.find(tag => !selectedById.has(tag.id));
    if (added && hasMetadata(added.id)) {
      openMetadataDialog(nextTags, added);
    }
  };

  const handleOpenChange = open => {
    setIsOpen(open);
    if (!open) setSearch("");
  };

  let emptyContent = "No options found.";
  if (optionsQuery.isLoading) emptyContent = <Spinner />;
  else if (optionsQuery.isError) {
    emptyContent = (
      <Button
        type="button"
        variant="ghost"
        size="xs"
        onClick={optionsQuery.refetch}
      >
        Could not load tags. Retry
      </Button>
    );
  }

  return (
    <Combobox
      items={options}
      multiple
      value={selectedIds}
      onValueChange={handleValueChange}
      inputValue={search}
      onInputValueChange={setSearch}
      onOpenChange={handleOpenChange}
      filter={null}
      itemToStringLabel={option => option.name}
      disabled={disabled}
    >
      <MultipleFilterTrigger
        id={inputId}
        aria-invalid={invalid ? true : undefined}
        onBlur={onBlur}
        className={TRIGGER_IN_FORM}
        getTagStyle={styleFor}
        labelFor={labelFor}
        keyFor={tagId => tagId}
        renderBadgeAddon={tagId => hasMetadata(tagId) && <MetadataIcon />}
        placeholder={placeholder}
      />
      <ComboboxContent>
        <ComboboxChips
          render={<div ref={chipsRef} onScroll={onChipsScroll} />}
          className={clsx(
            !selected.length && "tw:min-h-0 tw:p-0",
            "tw:max-h-44 tw:overflow-y-auto"
          )}
        >
          <ComboboxValue>
            {chips =>
              chips.map(tagId => (
                <ComboboxChip
                  key={tagId}
                  style={styleFor(tagId)}
                  showRemove={!isLocked(tagId)}
                  className="tw:h-auto tw:max-w-full tw:min-w-0 tw:justify-start tw:text-left tw:whitespace-normal"
                >
                  {hasMetadata(tagId) ? (
                    <button
                      type="button"
                      title="Edit tag metadata"
                      onClick={() => editMetadata(tagId)}
                      className="tw:flex tw:min-w-0 tw:cursor-pointer tw:items-center tw:gap-1 tw:underline tw:decoration-dotted tw:underline-offset-2 tw:wrap-anywhere"
                    >
                      {labelFor(tagId)}
                      <MetadataIcon />
                    </button>
                  ) : (
                    <span className="tw:min-w-0 tw:wrap-anywhere">
                      {labelFor(tagId)}
                    </span>
                  )}
                </ComboboxChip>
              ))
            }
          </ComboboxValue>
        </ComboboxChips>
        <ComboboxInput showTrigger={false} placeholder="Search" />
        <ComboboxEmpty>{emptyContent}</ComboboxEmpty>
        <ComboboxList className="tw:scroll-fade">
          <ComboboxCollection>
            {item => (
              <ComboboxItem key={item.id} value={item.id}>
                <Badge
                  style={styleFor(item.id)}
                  className="tw:h-auto tw:max-w-full tw:min-w-0 tw:shrink tw:justify-start tw:text-left tw:whitespace-normal tw:wrap-anywhere"
                >
                  {item.name}
                  {withMetadata && item.has_metadata && <MetadataIcon />}
                </Badge>
              </ComboboxItem>
            )}
          </ComboboxCollection>
          {options.length > 0 && (
            <NextPageStatus
              sentinelRef={sentinelRef}
              isFetchingNextPage={optionsQuery.isFetchingNextPage}
              isNextPageError={optionsQuery.isFetchNextPageError}
              hasLoadedAll={optionsQuery.isSuccess && !optionsQuery.hasNextPage}
              onRetry={optionsQuery.fetchNextPage}
            />
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};

FormTag.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  required: PropTypes.bool,
  modelType: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  withMetadata: PropTypes.bool,
  id: PropTypes.string,
};

export default FormTag;
